/* eslint no-console: ["error", { allow: ["error"] }] */
import React, { useEffect } from "react";
import {
  PackageFeature,
  PackageFeatureList,
  LoadingTextBlock
} from "src/components/video-lab/plan-cards/componetry";
import {
  PackageVariant,
  IPlanCardData
} from "src/components/video-lab/plan-cards/utils";
import DtvDivider from "@directv-digital/dds.ui.dtv-divider";
import DtvText, { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";
import {
  colorPalette,
  parseColor,
  MBL_SIZE,
  TBL_SIZE
} from "@directv-digital/dds.ui.dtv-theme";
import FusionTheme from "@directv-digital/fusion.themes.fusion";
import DtvTypography from "@directv-digital/dds.ui.dtv-typography";
import ButtonPanel from "@directv-digital/dds.ui.button-panel";
import CardMedia from "@mui/material/CardMedia";
import DtvAvatar from "@directv-digital/dds.ui.dtv-avatar";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton, { IDtvButtonVariation } from "@directv-digital/dds.ui.dtv-button";
import { EqualHeightElement, EqualHeightContext } from "react-equal-height/clean";
import { getAnalyticsAttrs, fireLeadCaptureEventGA } from "@directv-digital/dds.services.analytics-service";
import type { TypographyVariants } from "@mui/material/styles";
import { ComparePkgTray } from "../../ComparePackageTray";
import type { DpAllInData } from "lib/helper/dynamic-injection/all-in-rsn";
import { useLiveAllIn } from "src/hooks/useLiveAllIn";
import { ProductJsonLd } from "src/components/ProductJsonLd";
import { stripHtmlTags, type ProductJsonLdInput } from "src/utils/productJsonLd";

const fusionPalette = FusionTheme.fusion.palette;

/**
 * A `DtvText` whose copy carries a dynamic-pricing all-in — the ONLY subscriber
 * to the live per-zip RSN on a plan card.
 *
 * Kept as its own component deliberately: the subscription sits on the leaf that
 * renders the text, so changing zip re-renders this one line and nothing above
 * it (no card re-render, no card-set re-render, no page re-render). Without
 * `dpAllIn` it is a plain `DtvText` — the hook returns the server string and the
 * effect settles on the first pass.
 *
 * THE TOOLTIP IS RE-PRICED TOO. It comes from the same CMS `item` as the visible
 * copy and goes through the same server-side token injection, so it can carry its
 * own resolved `OFFER<n>ALLIN`. `buildDpAllIn` is all-or-nothing at the tier level
 * precisely to avoid a mixed-zip contradiction inside one block of regulated copy;
 * that invariant breaks one level up if the price line re-prices to the customer's
 * zip while the tooltip explaining "w/ req'd fees" still states the default-zip
 * number. Both go through the same rewrite, or neither does.
 *
 * `aria-live="polite"` + `aria-atomic="true"`: this text changes AUTOMATICALLY
 * shortly after first paint, triggered by a zip entered elsewhere on the page, and
 * what changes is a regulated price. A screen-reader user who already heard the
 * original number gets no notification otherwise — WCAG 2.2 SC 4.1.3 (Status
 * Messages, AA). `polite` announces without stealing focus.
 *
 * THE LIVE REGION IS OPT-IN AND NARROW, deliberately. It is requested only by the
 * PRICE line (`announceReprice`) and only when `dpAllIn` actually carries tiers, i.e.
 * only where the text can really change. Making it unconditional was worse than the
 * silence it replaced: this component renders once per `priceCopy` item AND once for
 * `legalContent`, so a 3-4 card page became 6-8 ATOMIC polite regions all mutating on
 * the same tick. Screen readers queue polite announcements instead of coalescing them
 * and `aria-atomic` forces each region to be re-read IN FULL, so one zip entry read
 * out every card's price line plus its entire legal paragraph, back to back, with no
 * way to skip. The price is the announcement that carries the information; re-reading
 * the legal paragraph adds nothing the customer can act on. It also left every
 * RSN-exempt card as a permanent live region whose text can never change.
 *
 * If the legal change ever does need announcing, the accessible form is ONE
 * page-level `role="status"` saying "Prices updated for ZIP …" — not N regions each
 * reciting their own paragraph. (WAT-11203.)
 */
export const LiveAllInText = ({
  content,
  dpAllIn,
  tooltip,
  announceReprice,
  ...textProps
}: {
  content: string;
  dpAllIn?: DpAllInData;
  /** Opt in to the SC 4.1.3 live region. Price copy only — never the legal block. */
  announceReprice?: boolean;
} & Omit<IDtvTextProps, "content">) => {
  const liveContent = useLiveAllIn(content, dpAllIn);
  const liveTooltipContent = useLiveAllIn(tooltip?.content, dpAllIn);
  // Gate on tiers, not on `dpAllIn` being present: a contract with an empty ladder
  // can never re-price, so announcing it is a promise the component cannot keep.
  const isLive = Boolean(announceReprice && dpAllIn?.tiers?.length);
  // `content` LAST is belt-and-braces, NOT the load-bearing guarantee. The real one is
  // MECHANICAL: `content` is destructured out of the props above, so `textProps`
  // provably cannot carry one — a stray `content` in a caller's spread lands on the
  // named param, not in the rest — which is exactly why REORDERING the spread here is a
  // behaviour-identical mutant and no test can pin it. What IS pinned (spec: "a
  // `content` in the caller's spread never wins") is the contract that matters: the
  // rendered text is always the live-derived string. Do NOT lean on the
  // `Omit<IDtvTextProps, "content">` typing for any of this — the repo's `tsc` does not
  // currently type-check (parse errors in xstate + two Select.tsx files suppress the
  // whole semantic pass).
  // The two aria attributes go BEFORE the spread so a caller can still override them;
  // `content` stays last for the reason above.
  return (
    <DtvText
      aria-live={isLive ? "polite" : undefined}
      aria-atomic={isLive ? "true" : undefined}
      {...textProps}
      tooltip={tooltip?.content ? {
        ...tooltip,
        content: liveTooltipContent
      } : tooltip}
      content={liveContent}
    />
  );
};

/**
* IDtvPlanCardProps = all properties added for rendering by module
* IPlanCardData = includes all properties sent from server side
*/
export interface IDtvPlanCardProps extends IPlanCardData {
  id: any;
  cardPosition: number;
  packageCardVariant: string;
  isMduVariant: boolean;
  showNoLocalChannels: boolean;
  queryparam?: string;
  disableEqualHeight: boolean;
  analytics: {
    ga?: any;
    ga4Impressions?: any;
  };

  /** Topper */
  highlightBarColor?: string;
  noTopBorder?: boolean;
  disableAutoTopper?: boolean;
  updateTopperHeightFn?: (index : number, height: number) => void;

  /** Top Section */
  channelLogoStyles?: any;
  channelLogoContainerStyles?: any;
  channelLogoImageWrapperStyles?: any;
  lazyLoadImage?: boolean;

  /** Pricing */
  isLoadingPrices: boolean;
  /**
   * Dynamic-pricing service (WAT-11203): base + default-zip RSN behind each
   * all-in the server painted into this card's copy, so the price copy / legal
   * can re-price themselves for the customer's zip. Undefined = nothing to
   * recompute (RSN-exempt package, or no dynamic tokens authored).
   */
  dpAllIn?: DpAllInData;

  /** CTAs */
  packageChange?: any;

  /** Pulldown list */
  open?: boolean;
  onComparePackagesClick: (index) => void;

  /** Mdu Options */
  selectedPackage?: string;
  setSelectedPackage?: any;

  /** Embedded SVODs */
  embeddedSvods?: any;
  embeddedSvodSection?: boolean;
  comparePkgTray?: ComparePkgTray;
};

const ribbonStyles = {
  ribbonContainer: {
    position: "relative",
    display: "inline-block",
    margin: "18px 16px 10px"
  },
  ribbonLayer: {
    "--f": ".5em", /* control the folded part */
    "--r": {
      xs: ".8em",
      sm: ".8em",
      md: "1.1em"
    },
    position: "absolute",
    left: {
      xs: "calc(-3.25*var(--f))",
      sm: "calc(-3.25*var(--f))",
      md: "calc(-2.5*var(--f))"
    },
    "@media (min-width: 1200px) and (max-width: 1700px)": {
      left: "calc(-2.75*var(--f))"
    },
    lineHeight: "1.8",
    clipPath:
      "polygon(0 0,0 calc(100% - var(--f)),var(--f) 100%, var(--f) calc(100% - var(--f)),100% calc(100% - var(--f)),calc(97% - var(--r)) calc(50% - var(--f)/2),100% 0)",
    width: "100%",
    // width: {
    //   xs: "310px",
    //   sm: "231px",
    //   md: "235px",
    //   lg: "265px",
    //   xl: "340px",
    // },
    height: "auto",
    borderBottom: "8px solid #AD9A52",
    padding: "0 23px 4px 27px",
    boxShadow: "0px 2px 20px rgba(0, 0, 0, 0.25)",
    textAlign: "left"
  }
};

const getColor = (color: string) => {
  if (color === "" || color === undefined || color === null) {
    return "";
  }
  let res = fusionPalette?.[`${color}`];
  if (res) {
    return res;
  }
  res = parseColor(color);
  if (res) {
    return res;
  }
  if (color?.startsWith("#") || ["rgb", "hsl", "transparent", "none"]?.some(other => color?.toLowerCase()?.includes(other))) {
    return color;
  }
  return res;
};

export enum SchemaTypes {
  PRODUCT = 'https://schema.org/Product',
  BRAND = 'https://schema.org/Brand',
  OFFER = 'https://schema.org/Offer',
}

export enum SchemaProps {
  NAME = 'name',
  IMAGE = 'image',
  DESCRIPTION = 'description',
  URL = 'url',
  PRICE_CURRENCY = 'priceCurrency',
  PRICE = 'price',
  PRICE_VALID_UNTIL = 'priceValidUntil',
  AVAILABILITY = 'availability',
}

/* Channel Compare Tool Modal url config — derives a copy instead of mutating
 * the CMS-fed prop object (card data objects are shared across card sets).
 * Exported for unit tests only — intentionally not re-exported from index.ts */
export const getSeeAllChannelsCtaWithLocalsFlag = (cta: any, showNoLocalChannels: boolean) => {
  const url = cta?.modalData?.url;
  if (!showNoLocalChannels || !url || url?.includes?.('isLocal')) {
    return cta;
  }
  return {
    ...cta,
    modalData: {
      ...cta.modalData,
      url: `${url}${url?.includes?.('?') ? '&' : '?'}isLocal=false`
    }
  };
};

// eslint-disable-next-line complexity
export function DtvPlanCard(props: IDtvPlanCardProps) {
  try {
    const {
      id,
      // variant,
      cardPosition,
      packageCardVariant,
      isMduVariant,
      analytics,
      showNoLocalChannels,
      highlightBarColor,
      backgroundColor,
      cardFontColor,
      disableEqualHeight,
      /** Topper */
      topper,
      noTopBorder,
      disableAutoTopper,
      updateTopperHeightFn,
      rewardTextColor,
      /** Top Section */
      productName,
      channels,
      channelLogoStyles,
      channelLogoContainerStyles,
      channelLogoImageWrapperStyles,
      lazyLoadImage,
      seeAllChannelsCta,
      ribbon,
      /** Pricing */
      productDesc,
      productPrice,
      isLoadingPrices,
      dpAllIn,
      /** CTAs */
      cta,
      packageChange,
      optionalCTA,
      /** Bottom section */
      whatYouGet,
      open,
      comparePackagesCta, onComparePackagesClick,
      featureHeading,
      features,
      legalContent,
      /** MDU Options */
      selectedPackage,
      setSelectedPackage,
      /** Embedded SVODs */
      embeddedSvods,
      embeddedSvodSection,
      queryparam
    } = props;

    const topperContent = topper?.content;
    const topperTextColor = topperContent?.color || rewardTextColor || colorPalette.lightLapis?.main;

    const isProgressivePkgCard = packageCardVariant === PackageVariant.v2;
    const textColor = getColor(cardFontColor || 'jet.main');
    const productNameSchema = stripHtmlTags(productName?.content);
    const productDescSchemaContent = stripHtmlTags(productDesc?.content);
    const featuresSchemaContent =
      features
        ?.map(feature => stripHtmlTags(feature?.content))
        .filter(Boolean)
        .join(', ') || "";

    /* Topper Height Update */
    const topperRef = React.useRef(null);
    useEffect(() => {
      updateTopperHeightFn?.(cardPosition, topperRef?.current?.clientHeight)
      // measure once on mount; a ref identity never changes so [topperRef] was equivalent
    }, [])

    /* Pricing Config */
    const {
      priceLabel,
      price,
      strikeoutPrice,
      priceCopy
    } = productPrice;
    const displayPrice = price?.moddedContent || price?.content || "";
    const strikeoutDisplay = strikeoutPrice?.moddedContent || strikeoutPrice?.content;

    // HIM-4785: pre-rendered Product JSON-LD (replaces the old inline schema.org microdata).
    // Built from the card's own data; content implementors can override/fill any field via the
    // optional CMS `schemaOrg` prop (e.g. a brand/image/price not present on the card itself).
    const schemaOfferUrl =
      cta?.targeturl ||
      props?.ctaPanel?.ctaList?.[0]?.targeturl ||
      optionalCTA?.targeturl ||
      "";
    const productSchemaInput: ProductJsonLdInput = {
      name: productNameSchema,
      description: productDescSchemaContent || featuresSchemaContent,
      url: schemaOfferUrl,
      price: displayPrice,
      ...((props as any)?.schemaOrg || {})
    };

    const { setForceUpdate: forceUpdate   } = React.useContext(EqualHeightContext);

    const resolvedSeeAllChannelsCta = getSeeAllChannelsCtaWithLocalsFlag(seeAllChannelsCta, showNoLocalChannels);

    /* Analytics */
    const ctaPanelGaData = props?.ctaPanel?.ctaList?.map(item => getAnalyticsAttrs(item?.label, "panel_plan card", item?.targeturl || item?.href)) || [];
    const ga4Analytics = analytics?.ga4Impressions;

    const fireModalOpenAnalytics = (name: string) => {
      fireLeadCaptureEventGA({
        event: 'trackEvent',
        event_name: 'modal_status',
        dim_name: 'open',
        dim_value: name
      });
    }
    const handleComparePackagesCtaClick = cardPosition => {
      onComparePackagesClick(cardPosition);
      fireModalOpenAnalytics('modal_compare packages');
    }

    return (
        <DtvBox
          id={`${id}-card-ctnr`}
          sx={{
            height: "100%",
            ...(!isMduVariant ? {
              borderRadius: "13px 13px 5px 5px",
              borderLeft: `3px solid ${topper ? highlightBarColor : "transparent"}`,
              borderRight: `3px solid ${topper ? highlightBarColor : "transparent"}`,
              borderBottom: `10px solid ${topper ? highlightBarColor : "transparent"}`,
              boxShadow: topper && `0px 2px 20px #00000040`,
              ...(isProgressivePkgCard && {
                borderLeft: "transparent",
                borderRight: "transparent",
                borderBottom: "transparent",
                borderRadius: "0px",
                boxShadow: "none"
              })
            } : {
              borderRadius: "13px 13px 7px 7px",
              borderLeft: `3px solid ${selectedPackage === productName?.content && highlightBarColor}`,
              borderRight: `3px solid ${selectedPackage === productName?.content && highlightBarColor}`,
              borderBottom: `3px solid ${selectedPackage === productName?.content && highlightBarColor}`
            })
          }}>
          <ProductJsonLd id={`${id}-card-ctnr-schema`} {...productSchemaInput} />
          {  (!noTopBorder || (noTopBorder && topper)) && (!disableAutoTopper || (disableAutoTopper && topper)) &&
          <DtvBox ref={topperRef}>
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-topper">
              <DtvBox
                id={`${id}-topper-ctnr`}
                sx={{
                  display: noTopBorder ? "none" : "block",
                  borderRadius: noTopBorder ?  "6.5px 6.5px 0px 0px" : "10px 10px 0px 0px",
                  backgroundColor: highlightBarColor,
                  height: topper ? "100%" : "5px",
                  padding: topper ?
                    topperContent?.image?.hasRewardCard ?
                      "4px 20px" :
                      "10px 0 5px 20px" :
                    0,
                  ...(isProgressivePkgCard && topper && {
                      minHeight: "26px",
                      padding: topper?.content?.image?.hasRewardCard ?
                        "4px 20px" :
                        "10px 16px 5px 16px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }),
                  ...(!isProgressivePkgCard &&
                    topper?.content?.image?.hasRewardCard && {
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    })
                }}>
                  {topper &&
                    <DtvBox component="div"
                      sx={{
                        ...(topper?.content?.image?.hasRewardCard && {
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          justifyContent: "center",
                          alignItems: "center"
                        })
                      }}>
                      <DtvBox sx={{
 display: "flex",
alignItems: "center",
flexWrap: "wrap"
}}>
                        {/* Generated by Copilot- Start - 2026-04-20 17:30:45 */}
                        {topperContent?.beforeText && (
                          <DtvText
                            color={topperTextColor}
                            content={topperContent.beforeText}
                            sx={{ mr: "0.25em" }}
                            variant="smalltextmedium"
                          />
                        )}
                        {topperContent?.spanText && (
                          <DtvText
                            color={topperContent.spanText.color}
                            content={topperContent.spanText.content}
                            variant="smalltextmedium"
                          />
                        )}
                        {/* Generated by Copilot- End - 2026-04-20 17:30:45 */}
                        <DtvText
                          color={topperTextColor}
                          component={productName?.component || "div"}
                          content={topperContent?.content || (!topperContent?.spanText && !topperContent?.beforeText ? '&nbsp;' : '')}
                          id={`${id}-topper`}
                          tooltip={{ ...topperContent?.tooltip }}
                          variant="smalltextmedium"
                          sx={{
                            ...(topperContent?.image?.hasRewardCard && {
                              display: "flex",
                              alignItems: "center"
                            })
                          }}
                        />
                      </DtvBox>
                      {topperContent?.image?.imageUrl && (
                        <CardMedia
                          alt={topperContent.image.altText || `topper image`}
                          component="img"
                          decoding="async"
                          key={`image-${topperContent.image.position || "right"}-${id}-topper`}
                          loading={lazyLoadImage ? "lazy" : "eager"}
                          src={topperContent.image.imageUrl}
                          sx={{
                            height: "41px",
width: "64px",
                            marginLeft: topperContent.image.position === "right" ? "10px" : 0,
                            marginRight: topperContent.image.position === "left" ? "10px" : 0
                          }}
                        />
                      )}
                    </DtvBox>
                  }
              </DtvBox>
            </EqualHeightElement>
          </DtvBox>}

          <DtvBox
            id={`${id}-card-body`}
            sx={{
            padding: "20px 20px 32px 20px",
            ...(topper && { padding: "24px 20px 32px 20px" }),
            ...(isProgressivePkgCard && {
              padding: noTopBorder ?
                "0 16px 28px 16px" :
                "24px 16px 28px 16px",
              ...(isMduVariant && { padding: "24px 16px 0" })
            })
            }}>
            {productName?.content && (
              <DtvBox sx={{
                backgroundColor,
                margin: noTopBorder ? "0 -16px 16px -16px" : "0",
                padding: noTopBorder ? "24px 0px 16px 16px" : "0",
                height: noTopBorder ? "fit-content" : "auto"
              }}>
                <EqualHeightElement disable={disableEqualHeight} name="plan-card-name">
                  <React.Fragment>
                    <DtvText
                      component={productName?.component || "h2"}
                      content={productName?.content}
                      id={`${id}-${cardPosition}-productname-ctnr`}
                      variant={isProgressivePkgCard ? "body1medium" : "body2medium"}
                      sx={{
                        color: `${productName?.color || textColor}`,
                        fontSize: {
 xs: "20px",
md: "24px"
},
                        fontWeight: 500,
                        letterSpacing: {
 xs: "-0.4px",
md: "-0.48px"
}
                      }}
                    />
                  </React.Fragment>
                </EqualHeightElement>
              </DtvBox>
            )}
            {
             <EqualHeightElement disable={disableEqualHeight} name="plan-card-svods">

               {embeddedSvodSection && embeddedSvods?.length > 0 ? (
               <DtvBox sx={{
 py: "20px",
minHeight: "100px"
}}>
                  <DtvText
                    component="p"
                    content={"Includes"}
                    variant="subhead1medium"
                    sx={{
                      fontSize: "14px !important",
                      fontFamily: "roboto-medium",
                      marginBottom: "5px"
                    }}
                  />
                  <DtvBox sx={{
 display: "grid",
gridTemplateColumns: "repeat(4, 1fr)",
gap: {
 xs: "16px",
sm: "8px",
md: "16px"
}
}} >
                      {embeddedSvods?.slice(0, 8).map((svod, index) => (!svod?.planCardsData?.disable ? <picture key={index} style={{
 display: "block",
width: "100%",
minWidth: 0
}}>
                          <source media={MBL_SIZE} srcSet={svod?.planCardsData?.icon?.mobile || svod?.planCardsData?.icon?.default} />
                          <source media={TBL_SIZE} srcSet={svod?.planCardsData?.icon?.tablet || svod?.planCardsData?.icon?.default} />
                          <CardMedia
                            alt={svod?.name || "streaming service"}
                            component="img"
                            decoding="async"
                            loading={"eager"}
                            src={svod?.planCardsData?.icon?.default}
                            sx={{
                              maxWidth: "64px",
                              maxHeight: "64px",
                              aspectRatio: "1 / 1",
                              borderRadius: "12px",
                              objectFit: "contain",
                              display: "block",
                              boxShadow: "0px 1px 2px 0px rgba(60, 64, 67, 0.3), 0px 1px 3px 1px rgba(60, 64, 67, 0.15)"
                            }}
                            onLoad={() => forceUpdate?.(value => !value)}
                            onError={e => {
                              console.error(`[Plancard][EmbeddedSvodImage] untracked icon error for index ${index}`, e);
                            }}
                          />
                        </picture> : null))}

                  </DtvBox>
                </DtvBox>
              ) : <DtvBox></DtvBox>
            }

             </EqualHeightElement>
            }
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-channels">
              {channels?.length > 0 && (
                <DtvBox
                  id={`${id}-channel-list-ctnr`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 64px))",
                    justifyContent: "center",
                    alignItems: "center",
                    margin: "20px 0 0 0",
                    gap: {
                      xs: isMduVariant ? "8px" : "12px",
                      sm: "12px"
                    },
                    ...channelLogoContainerStyles
                  }}>
                  {channels?.map((channel, index) => (
                    <DtvBox
                      key={index}
                      sx={{
                        minWidth: 0,
                        ...channelLogoImageWrapperStyles
                      }}
                    >
                      <CardMedia
                        alt={channel?.alt}
                        component="img"
                        decoding="async"
                        id={`${id}-channel-${index}`}
                        loading={lazyLoadImage ? "lazy" : "eager"}
                        src={channel?.url}
                        sx={{
                          width: "100%",
                          maxWidth: "64px",
                          maxHeight: "64px",
                          aspectRatio: "1 / 1",
                          objectFit: "contain",
                          display: "block",
                          ...channelLogoStyles
                        }}
                        onLoad={() => {
                          forceUpdate?.(value => !value);
                        }}
                      />
                    </DtvBox>
                  ))}
                </DtvBox>
              )}
            </EqualHeightElement>
            <DtvBox>
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-see-all-channels-cta">
              <React.Fragment>
                {resolvedSeeAllChannelsCta?.label && (
                  <DtvButton
                    useParsedLabel
                    googleAnalyticsData={getAnalyticsAttrs(resolvedSeeAllChannelsCta?.label?.replace(/<\/?[^>]+(>|$)/g, "")?.replace(/&nbsp;/g, ' '), "panel_plan card", "/modal/channel-lineup/")}
                    sx={{
                      margin: "8px 0 0",
                      px: 0,
                      width: "100%",
                      "&:focus": {
                        border: "2px solid #2E70BD"
                      }
                    }}
                    // @ts-expect-error id prop not declared on this DDS component but forwarded at runtime
                    id={`${id}-see-all-channels-cta`}
                    mode="dark"
                    typographyVariant="body3"
                    variant="uxlink"
                    {...resolvedSeeAllChannelsCta}
                  />
                )}
              </React.Fragment>
            </EqualHeightElement>
            {ribbon &&
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-ribbon">
              <DtvBox sx={{
 position: "relative",
width: "100%",
height: {
 xs: "52px",
md: "72px",
lg: "52px",
xl: "24px"
}
}}>
                <DtvBox sx={{
 position: "absolute",
top: "-1px",
width: "100%"
}}>
                  {ribbon && (ribbon?.content || ribbon?.cta) && (
                    <DtvBox id={`${id}-ribbon-ctnr`} sx={{
 ...ribbonStyles.ribbonContainer,
height: "auto",
width: "100%"
}}>
                      <DtvBox
                        sx={{
                          ...ribbonStyles.ribbonLayer,
                          backgroundColor: ribbon?.bgColor ? ribbon?.bgColor : "#EEBF08",
                          m: packageCardVariant === PackageVariant.v2 ?
                            "0px -16px 0px -16px" :
                            "0px -20px 0px -20px"
                        }}
                      >
                        {ribbon?.cta?.label && <DtvButton
                        useParsedLabel
                        googleAnalyticsData={getAnalyticsAttrs(ribbon?.cta?.label, "panel_plan card", ribbon?.cta?.targeturl)}
                        mode="dark"
                        typographyVariant={"Body 3" as TypographyVariants['Body 3']}
                        variant="uxlink"
                        sx={{
                          px: 0,
                          mr: "5px",
                          width: {
 xs: "auto",
sm: "auto",
md: "auto"
},
                          display: "inline",
                          fontSize: "16px !important",
                          fontWeight: "bold !important",
                          lineHeight: "0.5em",
                          verticalAlign: "-webkit-baseline-middle",
                          color: ribbon?.ctaColor ? ribbon?.ctaColor : "#000000",
                          "&:focus": {
                            border: "2px solid #2E70BD"
                          }
                        }}
                        {...ribbon?.cta}
                      />}
                      <DtvText
                        component="div"
                        content={ribbon?.content}
                        id={`${id}-ribbon`}
                        sx={{
                          color: ribbon?.textColor ? ribbon?.textColor : "#2E70BD",
                          fontSize: "16px !important",
                          lineHeight: "0.5em",
                          display: "inline",
                          verticalAlign: "-webkit-baseline-middle"
                        }}
                      />
                      </DtvBox>
                    </DtvBox>
                  )}
                </DtvBox>
              </DtvBox>
            </EqualHeightElement>
            }
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-description">
              <DtvBox id={`${id}-price-desc-ctnr`} sx={{ ...(productDesc?.content && { padding: "32px 0 0 0" }) }}>
                  <DtvText
                    component={productDesc?.component || "div"}
                    content={productDesc?.content}
                    cta={{ ...productDesc?.cta }}
                    id={`${id}-product-desc`}
                    sx={{ color: `${textColor}` }}
                    tooltip={{ ...productDesc?.tooltip }}
                    variant="subhead2"
                  />
              </DtvBox>
            </EqualHeightElement>
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-price-label">
            <React.Fragment>
              {(priceLabel?.content || strikeoutPrice?.content) ? (
                <DtvBox
                  display="flex"
                  id={`${id}-price-label-ctnr`}
                  sx={{
                    pt: {
 xs: "36px",
md: "40px"
},
                    ...(isProgressivePkgCard && {
                      paddingTop: "20px",
                      margin: "0 0 6px 0"
                    })
                  }}>
                  {priceLabel?.content && (
                    <DtvText
                      component={priceLabel?.component || "div"}
                      content={`${priceLabel?.content  } `} // 3 spaces padding between "See Details"
                      id={`${id}-product-price-label`}
                      sx={{ color: `${textColor}` }}
                      variant="body2"
                    />
                  )}
                  {strikeoutPrice?.content && (
                    <div role="alert">
                      <DtvTypography
                        sx={{
                          top: "auto",
                          width: "1px",
                          height: "1px",
                          overflow: "hidden"
                        }}>
                      </DtvTypography>
                      <DtvText
                        aria-hidden="true"
                        component={strikeoutPrice?.component || "div"}
                        content={strikeoutDisplay}
                        id={`${id}-strikeout-price`}
                        variant="body2"
                        sx={{
                          typography: "body2",
                          textDecoration: "line-through",
                          textDecorationColor: "#D23627"
                        }}
                      />
                    </div>
                  )}
                </DtvBox>
              ) : (
                <DtvBox sx={{ ...(isProgressivePkgCard && { paddingBottom: "0px" }) }} />
              )}
            </React.Fragment>
            </EqualHeightElement>
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-price-value">
              {displayPrice && (
                <DtvBox
                  alignItems="baseline"
                  display="flex"
                  id={`${id}-prod-price-ctnr`}
                  sx={{ margin: "8px 0 0 0" }}
                >
                  <DtvText
                    content={displayPrice?.split(".")?.[0]}
                    variant="pricing"
                    sx={{
 typography: "pricing",
color: `${textColor}`
}}
                  />
                    <DtvText
                      content={`.${displayPrice?.split?.(".")?.slice?.(1)?.join?.('.')}`}
                      variant="pricingsmall"
                      sx={{
 typography: "pricingsmall",
color: `${textColor}`
}}
                    />
                </DtvBox>
              )}
            </EqualHeightElement>
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-price-copy-container">
              {priceCopy?.length && priceCopy?.map((item, ix) => {
                try {
                  const showLoading = isLoadingPrices ||
                    !item?.moddedContent ||
                    item?.moddedContent?.includes("NaN") ||
                    item?.moddedContent?.includes("undefined");

                  return (
                    <DtvBox id={`${id}-dynamic-copy-${ix}`}
                      key={ix}
                      sx={{ lineHeight: "18.2px !important" }}
                    >
                      {showLoading ? (
                        <LoadingTextBlock content={item?.content}
                          tooltip={{ ...item?.tooltip }}
                          contentStyles={{
                            component: "p",
variant: "smalltext",
                            sx: {
                              display: "inline",
                              typography: "smalltext",
                              color: `${textColor}`,
                              fontWeight: "unset !important", // let the Loader Chunk component wrap <b> tags
                              letterSpacing: "-0.28px"
                            }
                          }}
                          cta={{
 inline: true,
...item?.cta
}}
                          dotsStyles={{
                            width: "35px",
                            margin: "0 4px 0 2px"
                          }}
                        />
                      ) : (
                        <LiveAllInText component={item?.component || "div"}
                          content={`${item?.moddedContent}`}
                          dpAllIn={dpAllIn}
                          // The ONLY live region on the card: the price line carries the
                          // number that changed. The legal block below re-prices too but
                          // is deliberately silent — see the LiveAllInText docblock.
                          announceReprice
                          tooltip={{ ...item?.tooltip }}
                          variant="smalltext"
                          cta={{
 inline: true,
...item?.cta
}}
                          sx={{
                            display: "inline",
                            typography: "smalltext",
                            mt: ix > 0 ? 1 : 0,
                            color: `${textColor}`, // "gray.300",
                            // WAT-11565: this price-copy CTA reaches DtvButton through
                            // DtvText, which sets modalType="legal" whenever the CMS
                            // supplies cta.legalText / cta.legalUrl, so it now renders
                            // as <button> instead of <a href="#">. An anchor-only
                            // selector would drop the ring with nothing failing.
                            "& a:focus, & button:focus": {
                              outline: "2px solid #2E70BD"
                            }
                          }}
                        />
                      )}
                    </DtvBox>
                  );
                } catch(error) {
                  console.error("[PlanCard] Error rendering price copy", error);
                  return null;
                }
              })}
            </EqualHeightElement>
            {!isProgressivePkgCard && (
              <DtvDivider my={8} sx={{
 color: "gray.100",
mt: "32px",
mb: "32px"
}} />
            )}
            {!isMduVariant && (
               <EqualHeightElement disable={disableEqualHeight} name="plan-card-cta-pannel">
                <>
                {props?.ctaPanel && (
                  <DtvBox id={`${id}-pkg-cta-panel-ctnr`} sx={{
                    display: "flex",
justifyContent: "center",
alignItems: "flex-start",
                    margin: "20px 0 0 0"
                    // ...((isMduVariant === true) && { m: 0 })
                  }}>
                    <ButtonPanel
                      fullWidth
                      ga4Analytics={[ga4Analytics]}
                      gap="12px"
                      googleAnalyticsData={ctaPanelGaData}
                      id={`${id}-pkg-cta-panel`}
                      alignment={[
                        {
 vertical: true,
mq: "xs"
},
                        {
 vertical: true,
mq: "sm"
}
                      ]}
                      ctaStyle={{
                        paddingTop: "14px",
                        paddingBottom: "16px",
                        ...(props?.ctaPanel?.ctaList?.length > 1 && {
                          padding: "12px 0 12px 0"
                        })
                        // ...(isMduVariant && { height: '56px' })
                      }}
                      {...props?.ctaPanel}
                      linkPosition="panel_plan card"
                      ctaList={
                        props?.ctaPanel?.ctaList ? props?.ctaPanel?.ctaList?.map(item => ({
                          ...item,
                          analytics: null,
                          targeturl: item?.targeturl?.includes("isLocals") ?
                            item.targeturl.replace(/([?&]isLocals=)(true|false)/, `$1${showNoLocalChannels}`) :
                            item?.targeturl
                        })) : []
                      }
                    />
                  </DtvBox>
                )}
                </>
              </EqualHeightElement>

            )}
            {!isMduVariant && isProgressivePkgCard ? (
              <PackageFeature
                comparePackagesCta={comparePackagesCta}
                disableEqualHeight={disableEqualHeight}
                features={features}
                id={`${id}-pkg-feature`}
                lazyLoadImages={lazyLoadImage}
                noTopBorder={noTopBorder}
                open={open}
                packageCardVariant={packageCardVariant}
                queryparam={queryparam}
                showNoLocalChannels={showNoLocalChannels}
                textColor={textColor}
                whatYouGet={whatYouGet}
                openComparePackagesModal={() => {
                  handleComparePackagesCtaClick(cardPosition);
                }}
              />
            ) : (
              <>
              <EqualHeightElement disable={disableEqualHeight} name="plan-card-future">
                <DtvBox sx={{ ...(isMduVariant && { mt: "20px" }) }}>
                  {featureHeading && (
                    <DtvText
                      content={featureHeading}
                      id={`${id}-feature-heading`}
                      variant="body2medium"
                    />
                  )}
                  <PackageFeatureList
                    comparePackagesCta={comparePackagesCta}
                    disableEqualHeight={disableEqualHeight}
                    features={features}
                    id={`${id}-pkg-featureList`}
                    isMduVariant={isMduVariant}
                    noTopBorder={noTopBorder}
                    packageCardVariant={packageCardVariant}
                    textColor={textColor}
                    openComparePackagesModal={() => {
                      handleComparePackagesCtaClick(cardPosition);
                    }}
                  />
                </DtvBox>
              </EqualHeightElement>
              </>
            )}
            {legalContent && (
              <LiveAllInText
                color = "#555"
                // No `||` fallback: this JSX is already inside `{legalContent && (…)}`,
                // so the fallback was unreachable — and it hardcoded a regulated price
                // literal that a future refactor could surface. (WAT-11203.)
                content={legalContent}
                dpAllIn={dpAllIn}
                variant="smalltextmedium"
                sx={{
 marginTop: "12px",
display: "block"
}}
              />
            )}
            {isMduVariant && selectedPackage !== `${productName.content}` && (

              <EqualHeightElement disable={disableEqualHeight} name="plan-card-cta-pannel">
                <>
                {props?.ctaPanel && (
                  <DtvBox sx={{
                    display: "flex",
justifyContent: "center",
alignItems: "flex-start",
                    margin: "20px 0 0 0",
                    ...(isMduVariant && { m: 0 })
                  }}>
                    <ButtonPanel
                      fullWidth
                      ga4Analytics={[ga4Analytics]}
                      gap="12px"
                      googleAnalyticsData={ctaPanelGaData}
                      id={`${id}-pkg-cta`}
                      linkPosition="panel_plan card"
                      alignment={[
                        {
 vertical: true,
mq: "xs"
},
                        {
 vertical: true,
mq: "sm"
}
                      ]}
                      ctaStyle={{
                        paddingTop: "14px",
                        paddingBottom: "16px",
                        ...(props?.ctaPanel?.ctaList?.length > 1 && { padding: "12px 0" }),
                        ...(isMduVariant && { height: '56px' })
                      }}
                      onClick={() => {
                        setSelectedPackage(`${productName?.content}`);
                        packageChange(props?.pkgUrl, `${productName?.content}`);
                      }}
                      {...props?.ctaPanel}
                      ctaList={
                        props?.ctaPanel?.ctaList ? props?.ctaPanel?.ctaList?.map(item => ({
                          ...item,
                          analytics: null,
                          targeturl: item?.targeturl?.includes("isLocals") ?
                            item.targeturl.replace(/([?&]isLocals=)(true|false)/, `$1${showNoLocalChannels}`) :
                            item?.targeturl
                        })) : []
                      }

                    />
                  </DtvBox>
                )}
                </>
              </EqualHeightElement>
            )}
            {isMduVariant && selectedPackage !== `${productName.content}` && (
              <EqualHeightElement disable={disableEqualHeight} name="plan-card-cta-pannel">
                <DtvBox
                  sx={{
                    backgroundColor: "#1E815D",
                    display: "flex",
                    height: "80px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                    borderRadius: "0px 0px 5px 5px",
                    margin: "0 -16px"
                  }}>
                  <DtvAvatar
                    alt="checkmark"
                    src="https://www.directv.com/dtvassets/global/icons/fusion/fusion-checkmark-primary-inverse-white.svg"
                    sx={{
 width: "24px",
height: "24px"
}}
                  />
                  <DtvText
                    color="#FFF"
                    content={"Your current selection"}
                    variant="body2medium"
                  />
                </DtvBox>
              </EqualHeightElement>
            )}
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-cta-button">
              <>
              {cta && (
                <DtvBox
                  id={`${id}-cta-btn-ctnr`}
                  sx={{
 display: "flex",
justifyContent: "center",
mt: {
 xl: 7,
lg: 3,
md: 3,
sm: 3,
xs: 3
}
}}>
                  <DtvButton
                    ga4Analytics={ga4Analytics}
                    googleAnalyticsData={getAnalyticsAttrs(cta?.label, "panel_plan card", cta?.targeturl)}
                    id={`${id}-cta`}
                    mode="dark"
                    variant={(cta?.variant as IDtvButtonVariation) || "uxsecondary"}
                    sx={{
                      mt: 0,
                      px: 0,
                      width: "100%"
                    }}
                    {...cta}
                  />
                </DtvBox>
              )}
              </>
            </EqualHeightElement>
            <EqualHeightElement disable={disableEqualHeight} name="plan-card-call-cta-button">
              <>
              {optionalCTA && (
                <DtvBox
                  id={`${id}-opt-cta-ctnr`}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mt: {
 xl: 7,
lg: 3,
md: 3,
sm: 3,
xs: 3
}
                  }}>
                  <DtvButton
                    googleAnalyticsData={getAnalyticsAttrs(optionalCTA?.label, "panel_plan card", optionalCTA?.targeturl)}
                    id={`${id}-optionalCTA`}
                    label={optionalCTA.label || ""}
                    mode="dark"
                    target={optionalCTA.target || ""}
                    targeturl={optionalCTA.targeturl || ""}
                    variant={(optionalCTA?.variant as IDtvButtonVariation) || "uxsecondary"}
                    sx={{
                      mt: 0,
                      px: 0,
                      width: "100%"
                    }}
                    {...optionalCTA}
                  />
                </DtvBox>
              )}
              </>
            </EqualHeightElement>
            </DtvBox>
          </DtvBox>
        </DtvBox>
    );
  } catch (err) {
    console.error("## Plan Card Error", err);
    return <div data-component="Plan Card not visible" id="component-error" />;
  }
}
